import React, { useEffect, useRef, useState } from "react";
import "./PumpingTest.css";

// Gallery image imports — replace with your actual image files
import gallery1 from "../assets/gallery/Pgaller1.jpeg";
import gallery2 from "../assets/gallery/Pgaller2.jpeg";
import gallery3 from "../assets/gallery/Pgaller3.jpeg";
import gallery4 from "../assets/gallery/Pgaller4.jpeg";
import gallery5 from "../assets/gallery/Pgaller5.jpeg";
import gallery6 from "../assets/gallery/Pgaller6.jpeg";
import gallery7 from "../assets/gallery/Pgaller7.jpeg";
import gallery8 from "../assets/gallery/Pgaller8.jpeg";
import overviewImg from "../assets/gallery/pt-gallery-1.jpeg";

const GALLERY_IMAGES = [
  { src: gallery1},
  { src: gallery2},
  { src: gallery3},
  { src: gallery4},
  { src: gallery5},
  { src: gallery6},
  { src: gallery7},
  { src: gallery8},
];

/* ── Main PumpingTest Component ── */
const PumpingTest = () => {
  const [isVisible, setIsVisible] = useState({});
  const sectionRefs = useRef({});

  useEffect(() => {
    const observers = {};
    Object.keys(sectionRefs.current).forEach((key) => {
      observers[key] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [key]: true }));
          }
        },
        { threshold: 0.15 }
      );
      if (sectionRefs.current[key]) {
        observers[key].observe(sectionRefs.current[key]);
      }
    });
    return () => {
      Object.values(observers).forEach((obs) => obs.disconnect());
    };
  }, []);

  const setRef = (key) => (el) => {
    sectionRefs.current[key] = el;
  };

  const methodsData = [
    {
      number: "01",
      title: "Constant Rate Test",
      desc: "Continuous pumping at a fixed discharge rate to evaluate aquifer transmissivity, storativity, and long-term yield characteristics.",
      icon: "⬇",
    },
    {
      number: "02",
      title: "Step Drawdown Test",
      desc: "Incremental pumping rates applied to determine well efficiency, optimum yield, and well loss coefficients.",
      icon: "📈",
    },
    {
      number: "03",
      title: "Observation Well Monitoring",
      desc: "Multi-well array monitoring to track drawdown and recovery responses across the influence radius of the pumped well.",
      icon: "📡",
    },
    {
      number: "04",
      title: "Recovery Test",
      desc: "Post-pumping water level recovery measurement used to cross-validate aquifer parameters derived from drawdown data.",
      icon: "🔄",
    },
    {
      number: "05",
      title: "Water Quality Sampling",
      desc: "Groundwater sample collection during sustained pumping for chemical analysis and dewatering discharge compliance.",
      icon: "💧",
    },
    {
      number: "06",
      title: "Hydrogeological Reporting",
      desc: "Detailed analysis of pumping test data with aquifer parameter derivation and dewatering design recommendations.",
      icon: "📋",
    },
  ];

  return (
    <div className="pt-page" id="#pumping-test">
      {/* Top Banner */}
      <section className="pt-top-banner">
        <div className="pt-top-banner__grid" />
        <div className="pt-top-banner__glow" />
        <h1 className="pt-top-banner__title">
          Pumping <span className="pt-text-accent">Test</span>
        </h1>
      </section>

      {/* Overview Section */}
      <section
        className={`pt-overview ${isVisible["overview"] ? "pt-animate-in" : ""}`}
        ref={setRef("overview")}
      >
        <div className="pt-overview__inner">
          <div className="pt-overview__left">
            <div className="pt-section-label">
              <span className="pt-section-label__line" />
              WHAT WE DO
            </div>
            <h2 className="pt-section-title">
              UNDERSTANDING THE
              <br />
              <span className="pt-text-accent">AQUIFER BEHAVIOUR</span>
            </h2>
            <p className="pt-overview__text">
              Pumping test is a simulation study of expected water ingress during excavation or tunnel work. GSK Geotech's Pumping Test service measures how groundwater systems respond under controlled extraction, providing the data engineers need to design safe and efficient, design for shaft construction work and other underground design.
            </p>
            <p className="pt-overview__text">
              Our experienced team has executed pumping test works across
              major infrastructure and excavation projects, delivering accurate drawdown
              and recovery data and as per the requirements of the designers.
            </p>
            <div className="pt-overview__highlights">
              {[
                "Experienced testing team",
                "Step Discharge test, Constant Discharge test and Recovery phase",
                "End-to-end: setup, monitoring, reporting",
                "Compliant with industry & regulatory standards",
              ].map((item, i) => (
                <div key={i} className="pt-overview__highlight-item">
                  <span className="pt-overview__highlight-icon">✦</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
          <div className="pt-overview__right">
            <div className="pt-overview__image-block">
              <img
                src={overviewImg}
                alt="Pumping Well Setup Groundwater Testing Works"
                className="pt-overview__image"
              />
              <div className="pt-overview__stat-card">
                <span className="pt-overview__stat-number">2+</span>
                <span className="pt-overview__stat-text">PUMPING TESTS<br />COMPLETED</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methods Section */}
      <section
        className={`pt-methods ${isVisible["methods"] ? "pt-animate-in" : ""}`}
        ref={setRef("methods")}
      >
        <div className="pt-methods__inner">
          <div className="pt-section-label">
            <span className="pt-section-label__line" />
            OUR APPROACH
          </div>
          <h2 className="pt-section-title">
            TESTING
            <br />
            <span className="pt-text-accent">METHODS</span>
          </h2>
          <p className="pt-methods__subtitle">
            From well setup to data interpretation — we deploy a full suite of pumping
            test techniques tailored to each site's hydrogeological conditions.
          </p>
          <div className="pt-methods__grid">
            {methodsData.map((method, i) => (
              <div
                key={i}
                className="pt-method-card"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="pt-method-card__number">{method.number}</div>
                <div className="pt-method-card__icon">{method.icon}</div>
                <h3 className="pt-method-card__title">{method.title}</h3>
                <p className="pt-method-card__desc">{method.desc}</p>
                <div className="pt-method-card__line" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section
        className={`pt-process ${isVisible["process"] ? "pt-animate-in" : ""}`}
        ref={setRef("process")}
      >
        <div className="pt-process__inner">
          <div className="pt-section-label">
            <span className="pt-section-label__line" />
            HOW WE WORK
          </div>
          <h2 className="pt-section-title">
            OUR <span className="pt-text-accent">PROCESS</span>
          </h2>
          <div className="pt-process__steps">
            {[
              { step: "01", title: "Site Mobilisation", desc: "Pump rig setup, well installation, safety checks and permit acquisition prior to testing." },
              { step: "02", title: "Background Monitoring", desc: "Pre-test water level readings across all observation wells to establish baseline conditions." },
              { step: "03", title: "Pumping & Drawdown", desc: "Controlled extraction at constant or stepped rates with continuous drawdown monitoring." },
              { step: "04", title: "Recovery Monitoring", desc: "Post-pumping water level recovery tracked until conditions stabilise to baseline." },
              { step: "05", title: "Analysis & Reporting", desc: "Aquifer parameter derivation and detailed hydrogeological report with design recommendations." },
            ].map((item, i) => (
              <div key={i} className="pt-process__step">
                <div className="pt-process__step-num">{item.step}</div>
                <div className="pt-process__step-connector" />
                <div className="pt-process__step-content">
                  <h4 className="pt-process__step-title">{item.title}</h4>
                  <p className="pt-process__step-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Projects */}
      <section
        className={`pt-projects ${isVisible["projects"] ? "pt-animate-in" : ""}`}
        ref={setRef("projects")}
      >
        <div className="pt-projects__inner">
          <div className="pt-section-label">
            <span className="pt-section-label__line" />
            TRACK RECORD
          </div>
          <h2 className="pt-section-title">
            COMPLETED PUMPING
            <br />
            <span className="pt-text-accent">TEST PROJECTS</span>
          </h2>
          <div className="pt-projects__grid">
            {[
              { title: "Pumping test - N110", client: "LUM CHANG BUILDING CONTRACTORS PTE LTD" },
              { title: "Pumping test at DTSS Phase 2 Link Sewers Schedule 2 Contract 3B", client: "Megastones Holdings Pte Ltd" },
            ].map((proj, i) => (
              <div key={i} className="pt-project-card">
                <h4 className="pt-project-card__title">{proj.title}</h4>
                <p className="pt-project-card__client">{proj.client}</p>
              </div>
            ))}
          </div>
          <div className="pt-projects__cta">
            <a href="/projects" className="pt-btn pt-btn--outline">
              VIEW ALL PROJECTS →
            </a>
          </div>
        </div>
      </section>

      {/* Masonry Gallery */}
      <section className="pt-gallery">
        <div className="pt-gallery__inner">
          <div className="pt-section-label">
            <span className="pt-section-label__line" />
            SITE PHOTOS
          </div>
          <h2 className="pt-section-title">
            PROJECT <span className="pt-text-accent">GALLERY</span>
          </h2>
          <div className="pt-gallery__masonry">
            {GALLERY_IMAGES.map((img, i) => (
              <div key={i} className="pt-gallery__item">
                <img src={img.src} alt={img.caption} className="pt-gallery__img" />
                <div className="pt-gallery__overlay">
                  <span className="pt-gallery__caption">{img.caption}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="pt-cta">
        <div className="pt-cta__inner">
          <div className="pt-cta__left">
            <p className="pt-cta__label">READY TO START?</p>
            <h2 className="pt-cta__title">
              NEED A PUMPING TEST
              <br />
              <span className="pt-text-accent">FOR YOUR PROJECT?</span>
            </h2>
            <p className="pt-cta__desc">
              Our team is ready to mobilise. Get in touch for a detailed quote
              and consultation tailored to your project's groundwater requirements.
            </p>
          </div>
          <div className="pt-cta__right">
            <a href="/contact-us" className="pt-btn pt-btn--primary pt-btn--large">
              CONTACT US →
            </a>
            <div className="pt-cta__contacts">
              <span>Nedu: +65 9239 8494</span>
              <span>Suben: +65 9027 1160</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PumpingTest;